import { useState, useEffect } from "react";
import {
	Card,
	Checkbox,
	Progress,
	Button,
	Typography,
	Space,
	Flex,
} from "antd";
import {
	CheckCircleFilled,
	CheckCircleOutlined,
	CloseOutlined,
} from "@ant-design/icons";
import PrimaryButton from "../Buttons/PrimaryButton";

const { Text, Title, Paragraph } = Typography;

const onboardingSteps = [
	{ key: "connect_shopify", label: "Connect your Shopify store" },
	{ key: "generate_meta", label: "Generate AI-powered meta tags" },
	{
		key: "optimize_products",
		label: "Optimize at least 3 product descriptions",
	},
	{ key: "update_homepage", label: "Review & update your homepage SEO" },
	{ key: "explore_dashboard", label: "Explore the Performance Dashboard" },
];

const OnboardingChecklist = () => {
	const [completedSteps, setCompletedSteps] = useState(
		JSON.parse(localStorage.getItem("onboardingChecklist")) || {}
	);
	const [hidden, setHidden] = useState(
		localStorage.getItem("hideChecklist") === "true"
	);

	useEffect(() => {
		localStorage.setItem("onboardingChecklist", JSON.stringify(completedSteps));
	}, [completedSteps]);

	const handleCheck = (key) => {
		setCompletedSteps((prev) => ({
			...prev,
			[key]: !prev[key],
		}));
	};

	const handleDismiss = () => {
		setHidden(true);
		localStorage.setItem("hideChecklist", "true");
	};

	const completedCount = Object.values(completedSteps).filter(Boolean).length;
	const progressPercent = (completedCount / onboardingSteps.length) * 100;
	const allCompleted = completedCount === completedCount;

	if (hidden) return null;

	return (
		<Card
			title={
				<Space>
					🚀{" "}
					<Title level={4} style={{ margin: 0 }}>
						Get Started with SellBoost AI
					</Title>
				</Space>
			}
			extra={
				<Button
					type="text"
					icon={<CloseOutlined />}
					onClick={handleDismiss}
					size="small"
					style={{ color: "#888" }}
				/>
			}
			style={{
				marginBottom: 20,
				borderRadius: 12,
				boxShadow: "0 4px 12px rgba(0, 0, 0, 0.08)",
			}}
		>
			{/* Description Section */}
			<Paragraph type="secondary" style={{ marginBottom: 16 }}>
				Follow these steps to maximize your experience with SellBoost AI.
				Completing them will help you set up SEO-friendly product pages and
				track performance effectively.
			</Paragraph>

			<Flex gap="24px">
				<Progress
					percent={progressPercent}
					status={allCompleted ? "success" : "active"}
					strokeColor={allCompleted ? "#52c41a" : "#1890ff"}
					style={{ marginBottom: 12 }}
					type="circle"
				/>
				<Flex vertical wrap={true} gap={'4px'}>
					{onboardingSteps.map(({ key, label }) => (
						<Space>
							{completedSteps[key] ? (
								<CheckCircleFilled style={{ color: "#52c41a" }} />
							) : (
								<CheckCircleOutlined />
							)}
							<Text>{label}</Text>
						</Space>
					))}
				</Flex>
			</Flex>

			<Paragraph type="secondary" style={{ marginTop: 12 }}>
				Keep going! Completing these steps will improve your store's SEO and
				increase conversions.
			</Paragraph>
		</Card>
	);
};

export default OnboardingChecklist;
